import type { GroupLifeQuoteDetails } from '@/services/group-life';
import {
  isTerminalPortalStatus,
  PortalStatus,
} from '@/shared/enums/portal-status.enum';
import { useIsUnderwriterUser } from '@/shared/hooks';
import { toaster } from '@/shared/ui/toaster.component';
import { Button } from '@noorden-insurtech/alba-design-system';
import { useNavigate } from '@tanstack/react-router';
import { Calculator } from 'lucide-react';
import { useCallback } from 'react';
import { useSelectedBrokerWriteGuard } from '../../../../brokers/hooks';
import { useConfirmPmeProposal } from '../../../hooks/pme/use-confirm-pme-proposal.hook';
import { usePmeIncompleteLivesGuard } from '../../../hooks/pme/use-pme-incomplete-lives-guard.hook';
import { usePmeProposalDetailsViewModel } from '../../../hooks/pme/use-pme-proposal-details-view-model.hook';
import type { PmeProposalStepProps } from '../proposal.types';
import { PmeIncompleteLivesDialog } from './sections/pme-incomplete-lives-dialog.component';
import { PmeProposalDetailsSections } from './sections/pme-proposal-details-sections.component';
import { PmeStepFooter } from './sections/step-footer.component';

type Props = PmeProposalStepProps & {
  policyId: string;
  details?: GroupLifeQuoteDetails;
  productCode?: string;
  effectiveDate?: string;
  proposalNumber?: string;
  /** Disparado após add/edit/delete de vida. `requiresRecalculation` diz se a
   *  alteração muda a tarifa. */
  onLivesChange?: (outcome: { requiresRecalculation: boolean }) => void;
  /** Disparado ao finalizar a importação de vidas. `requiresRecalculation` é
   *  `false` quando a planilha só trocou nome/CPF. */
  onImportCompleted?: (outcome: { requiresRecalculation: boolean }) => void;
  /** Recálculo em andamento — sinalizado no card de prêmio, sem bloquear a página. */
  isRecalculating?: boolean;
  /** Dispara o recálculo do prêmio — botão "Calcular" quando a proposta ainda
   *  não tem cálculo (ex.: proposta clonada antes do primeiro recálculo). */
  onRecalculate?: () => void;
  /** Chamado quando o Confirmar encaminhou a proposta para a subscrição. */
  onSentToAnalysis?: () => void;
  /** Etapas anteriores ainda não concluídas — barram o Confirmar. */
  pendingStepNames?: string[];
};

/**
 * RESUMO da proposta PME: o corretor confere o detalhamento e as vidas, e segue
 * para a Assinatura pelo "Confirmar".
 *
 * O Confirmar é o PORTÃO DE SUBSCRIÇÃO da proposta: houve alteração desde a
 * última confirmação — nas vidas ou na própria proposta — e ele manda para a
 * análise (18), sem avançar para a Assinatura; não houve, e ele só leva a
 * proposta para "Em assinatura" (12) e avança. Quem decide é o back-end, pelo
 * marcador de alterações; o front só reage ao `sentToUnderwritingAnalysis`.
 *
 * Não dispara DocuSign (quem faz isso é o "Assinatura Digital" da etapa de
 * Assinatura) nem recalcula. O recálculo continua saindo de onde ele nasce, a
 * alteração de vida desta tela, pelo `onLivesChange`.
 */
export function StepPmeResume({
  onBack,
  onNext,
  canGoBack,
  isViewMode = false,
  policyId,
  details,
  productCode,
  effectiveDate,
  proposalNumber,
  onLivesChange,
  onImportCompleted,
  isRecalculating,
  onRecalculate,
  onSentToAnalysis,
  pendingStepNames = [],
}: Props) {
  const navigate = useNavigate();
  const { isUnderwriterUser } = useIsUnderwriterUser();
  const confirmProposal = useConfirmPmeProposal(policyId);
  // Corretora inativa: sem Confirmar (a Api barra com 403).
  const brokerGuard = useSelectedBrokerWriteGuard('proposal', 'other');

  const detailsSections = usePmeProposalDetailsViewModel({
    details,
    productCode,
    effectiveDate,
    hideAssistanceValue: true,
  });

  /**
   * Vida sem nome ou CPF barra o Confirmar: depois dele a proposta vai para "Em
   * assinatura" e as vidas deixam de ser editáveis — é aqui a última chance de
   * corrigir.
   */
  const { guard: hasIncompleteLives, dialogProps: incompleteLivesDialogProps } =
    usePmeIncompleteLivesGuard(detailsSections.lives);

  const handleConfirm = useCallback(() => {
    if (confirmProposal.isPending || brokerGuard.blocked) return;
    // A Api cobra o mesmo (422); barrar aqui evita a ida e diz o que falta.
    if (pendingStepNames.length > 0) {
      toaster.create({
        type: 'error',
        title: 'Proposta incompleta',
        description:
          pendingStepNames.length === 1
            ? `Conclua a etapa ${pendingStepNames[0]} antes de confirmar.`
            : `Conclua as etapas ${pendingStepNames.join(' e ')} antes de confirmar.`,
      });
      return;
    }
    if (hasIncompleteLives()) return;
    confirmProposal.mutate(undefined, {
      onSuccess: data => {
        // Alteração pendente: a proposta foi para o subscritor, não para a
        // assinatura. Avançar aqui levaria o corretor para uma etapa de
        // assinatura de uma proposta que ele não pode mais assinar.
        if (data.sentToUnderwritingAnalysis) {
          onSentToAnalysis?.();
          return;
        }
        // Único ponto em que a proposta é de fato gerada: as etapas foram
        // concluídas e ela segue para a assinatura.
        toaster.create({
          type: 'success',
          title: 'Proposta gerada com sucesso',
          description: 'Siga para a assinatura digital.',
        });
        void onNext?.();
      },
      onError: error =>
        toaster.create({
          type: 'error',
          title: 'Erro ao confirmar',
          description:
            error instanceof Error ? error.message : 'Tente novamente.',
        }),
    });
  }, [
    brokerGuard.blocked,
    confirmProposal,
    hasIncompleteLives,
    onNext,
    onSentToAnalysis,
    pendingStepNames,
  ]);

  // Sem cálculo (ex.: proposta clonada antes do primeiro recálculo) não há
  // prêmio: o botão "Calcular" assume o lugar do Confirmar.
  const isCalculated = Boolean(details?.calculation);
  const canEdit = !isViewMode;
  const isUnderAnalysis =
    Number(details?.statusPortal) === PortalStatus.QuoteUnderAnalysis ||
    Number(details?.statusPortal) === PortalStatus.ProposalInAnalysis;
  /**
   * Proposta encerrada (expirada/recusada): não há Confirmar. Sem o "Sair" o
   * rodapé ficaria vazio, já que o bloco de ações depende de `canEdit`.
   */
  const isTerminalProposal = isTerminalPortalStatus(details?.statusPortal);

  return (
    <div className='flex flex-col gap-10'>
      <PmeProposalDetailsSections
        sections={detailsSections}
        policyId={policyId}
        details={details}
        proposalNumber={proposalNumber}
        isUnderAnalysis={isUnderAnalysis}
        isRecalculating={isRecalculating}
        showUnderwritingFields={isUnderwriterUser === true}
        canManageLives={!isViewMode}
        onLivesChange={onLivesChange}
        onImportCompleted={onImportCompleted}
      />

      <PmeIncompleteLivesDialog {...incompleteLivesDialogProps} />

      <PmeStepFooter
        onBack={onBack}
        canGoBack={canGoBack}
        rightSlot={
          <div className='flex items-center gap-3'>
            {/* Gerar PDF e Carta Comercial NÃO ficam aqui: os documentos são do
                momento da ASSINATURA, e é lá que eles aparecem. No Resumo o
                corretor confere e confirma. */}
            {canEdit && (
              <>
                {!isCalculated && onRecalculate && (
                  <Button
                    type='button'
                    variant='secondary'
                    size='md'
                    className='flex items-center gap-2'
                    onClick={onRecalculate}
                    disabled={isRecalculating}
                  >
                    <Calculator className='h-4 w-4' />
                    {isRecalculating ? 'Calculando...' : 'Calcular'}
                  </Button>
                )}
                <Button
                  type='button'
                  variant='primary'
                  size='md'
                  className='rounded-xl px-8 py-2'
                  onClick={handleConfirm}
                  disabled={
                    isRecalculating ||
                    !isCalculated ||
                    confirmProposal.isPending ||
                    brokerGuard.blocked
                  }
                  title={brokerGuard.reason}
                  loading={confirmProposal.isPending}
                >
                  Confirmar
                </Button>
              </>
            )}
            {isTerminalProposal && (
              <Button
                type='button'
                variant='primary'
                size='md'
                className='rounded-xl px-8 py-2'
                onClick={() => void navigate({ to: '/proposals' })}
              >
                Sair
              </Button>
            )}
          </div>
        }
      />
    </div>
  );
}
